import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../context/ToastContext';
import { useDeleteFeaturedIngredientMutation } from '../../../services/endpoints/content/featuredIngredient';
import { Document } from '../../../types/content.type';
import { getErrorMessage } from '../../utils';

const DeleteFeaturedIngredientButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteFeaturedIngredient, { isLoading }] = useDeleteFeaturedIngredientMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteFeaturedIngredient(id).unwrap();
      addToast('Featured ingredient deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Featured Ingredient"
        message="Are you sure you want to delete this featured ingredient?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

export const featuredIngredientsColumns: ColumnDef<Document>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/content/featured-ingredients/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },

  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/featured-ingredients/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/content/featured-ingredients/${row.original.id}/edit`}
          underline
        />
        <DeleteFeaturedIngredientButton id={row.original.id} />
      </Stack>
    )
  }
];
